import React from 'react';
import { useTranslation } from 'react-i18next';
import { SidebarIcon } from './SidebarIcon';
import {
  createSidebarGroupId,
  normalizeLinkUrl,
  SidebarEntry,
  SidebarGroupEntry,
  SidebarLeafEntry,
  SidebarMoveTarget,
  MAX_GROUP_LABEL_LENGTH,
  MAX_LINK_LABEL_LENGTH,
  MAX_LINK_URL_LENGTH,
} from './sidebarLayout';
import type { WorkspaceNavItem } from './workspaceNavItems';

type Entry = SidebarEntry<WorkspaceNavItem>;
type Leaf = SidebarLeafEntry<WorkspaceNavItem>;
type Group = SidebarGroupEntry<WorkspaceNavItem>;

type SidebarLayoutEditorProps = {
  visible: Entry[];
  hidden: Leaf[];
  canReset: boolean;
  onMove: (id: string, target: SidebarMoveTarget) => void;
  onSetHidden: (id: string, hidden: boolean) => void;
  onAddGroup: (id: string, label: string) => void;
  onRenameGroup: (id: string, label: string) => void;
  onRemoveGroup: (id: string) => void;
  onAddLink: (label: string, url: string) => void;
  onRemoveLink: (id: string) => void;
  onReset: () => void;
  /** Omit to hide the Done button (e.g. when the editor is embedded in a settings page). */
  onDone?: () => void;
  /** Display label for a workspace entry; defaults to its translated built-in label. */
  labelFor?: (item: WorkspaceNavItem) => string;
  hint?: string;
  resetLabel?: string;
};

const GripIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
    <circle cx="9" cy="6" r="1.8" />
    <circle cx="15" cy="6" r="1.8" />
    <circle cx="9" cy="12" r="1.8" />
    <circle cx="15" cy="12" r="1.8" />
    <circle cx="9" cy="18" r="1.8" />
    <circle cx="15" cy="18" r="1.8" />
  </svg>
);

const EyeIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path d="M2.5 12s3.5-6.5 9.5-6.5S21.5 12 21.5 12s-3.5 6.5-9.5 6.5S2.5 12 2.5 12z" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
    <circle cx="12" cy="12" r="2.8" stroke="currentColor" strokeWidth="1.9" />
  </svg>
);

const EyeOffIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path d="M3.5 3.5l17 17" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
    <path d="M10 5.8A10.6 10.6 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a17.6 17.6 0 0 1-3.2 3.9M6.6 6.6C3.9 8.6 2.5 12 2.5 12s3.5 6.5 9.5 6.5c1.6 0 3-.4 4.2-1" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
  </svg>
);

const PencilIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path d="M14.5 5.5l4 4-8.2 8.2-4.8 1 1-4.8L14.5 5.5z" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
  </svg>
);

const CloseIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const PlusIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const countVisibleItems = (entries: Entry[]): number =>
  entries.reduce((count, entry) => {
    if (entry.kind === 'item') {
      return count + 1;
    }
    if (entry.kind === 'group') {
      return count + entry.children.filter(child => child.kind === 'item').length;
    }
    return count;
  }, 0);

export const SidebarLayoutEditor: React.FC<SidebarLayoutEditorProps> = ({
  visible,
  hidden,
  canReset,
  onMove,
  onSetHidden,
  onAddGroup,
  onRenameGroup,
  onRemoveGroup,
  onAddLink,
  onRemoveLink,
  onReset,
  onDone,
  labelFor,
  hint,
  resetLabel,
}) => {
  const { t } = useTranslation('nav');
  const [dragId, setDragId] = React.useState<string | null>(null);
  const [overId, setOverId] = React.useState<string | null>(null);
  const [renamingId, setRenamingId] = React.useState<string | null>(null);
  const [renameValue, setRenameValue] = React.useState('');
  const [addingLink, setAddingLink] = React.useState(false);
  const [linkLabel, setLinkLabel] = React.useState('');
  const [linkUrl, setLinkUrl] = React.useState('');
  const [linkError, setLinkError] = React.useState<string | null>(null);
  const renameInputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (renamingId) {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    }
  }, [renamingId]);

  const lastVisibleItem = countVisibleItems(visible) <= 1;

  const leafLabel = (leaf: Leaf): string => {
    if (leaf.kind === 'link') {
      return leaf.link.label;
    }
    return labelFor ? labelFor(leaf.item) : t(leaf.item.labelKey);
  };
  const groupLabel = (group: Group): string => group.group.label || t('untitledGroup');

  const resetDrag = () => {
    setDragId(null);
    setOverId(null);
  };

  const dropOn = (target: SidebarMoveTarget) => {
    if (dragId) {
      onMove(dragId, target);
    }
    resetDrag();
  };

  const dragHandlers = (entry: Entry, target: SidebarMoveTarget) => ({
    draggable: true,
    onDragStart: (event: React.DragEvent) => {
      event.stopPropagation();
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', entry.id);
      setDragId(entry.id);
    },
    onDragOver: (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = 'move';
      if (overId !== entry.id) {
        setOverId(entry.id);
      }
    },
    onDrop: (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      dropOn(target);
    },
    onDragEnd: resetDrag,
  });

  const rowClass = (entry: Entry, extra = '') => [
    'sidebar-layout-row',
    extra,
    dragId === entry.id ? 'dragging' : '',
    overId === entry.id && dragId !== null && dragId !== entry.id ? 'drag-over' : '',
  ].filter(Boolean).join(' ');

  const handleLeafKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    leaf: Leaf,
    siblings: Entry[],
    index: number,
    parent: Group | null,
  ) => {
    let target: SidebarMoveTarget | null = null;

    switch (event.key) {
      case 'ArrowUp':
        if (index > 0) {
          target = { kind: 'before', anchorId: siblings[index - 1].id };
        } else if (parent) {
          target = { kind: 'before', anchorId: parent.id };
        }
        break;
      case 'ArrowDown':
        if (index < siblings.length - 1) {
          target = { kind: 'after', anchorId: siblings[index + 1].id };
        } else if (parent) {
          target = { kind: 'after', anchorId: parent.id };
        }
        break;
      case 'ArrowRight': {
        const previous = !parent && index > 0 ? siblings[index - 1] : null;
        if (previous && previous.kind === 'group') {
          target = { kind: 'into', groupId: previous.id };
        }
        break;
      }
      case 'ArrowLeft':
        if (parent) {
          target = { kind: 'after', anchorId: parent.id };
        }
        break;
      default:
        return;
    }

    if (target) {
      event.preventDefault();
      onMove(leaf.id, target);
    }
  };

  const handleGroupKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, group: Group, index: number) => {
    if (event.key === 'ArrowUp' && index > 0) {
      event.preventDefault();
      onMove(group.id, { kind: 'before', anchorId: visible[index - 1].id });
    } else if (event.key === 'ArrowDown' && index < visible.length - 1) {
      event.preventDefault();
      onMove(group.id, { kind: 'after', anchorId: visible[index + 1].id });
    }
  };

  const startRename = (group: Group) => {
    setRenamingId(group.id);
    setRenameValue(group.group.label);
  };

  const commitRename = () => {
    if (renamingId) {
      onRenameGroup(renamingId, renameValue);
    }
    setRenamingId(null);
  };

  const submitLink = () => {
    const url = normalizeLinkUrl(linkUrl);
    if (!url) {
      setLinkError(t('linkInvalidUrl'));
      return;
    }

    onAddLink(linkLabel, url);
    setAddingLink(false);
    setLinkLabel('');
    setLinkUrl('');
    setLinkError(null);
  };

  const renderLeafRow = (leaf: Leaf, siblings: Entry[], index: number, parent: Group | null) => {
    const label = leafLabel(leaf);
    const icon = leaf.kind === 'item' ? leaf.item.icon : 'external';
    const hideDisabled = leaf.kind === 'item' && lastVisibleItem;

    return (
      <li key={leaf.id} className={rowClass(leaf)} {...dragHandlers(leaf, { kind: 'before', anchorId: leaf.id })}>
        <button
          type="button"
          className="sidebar-layout-handle"
          aria-label={t('reorderItem', { label })}
          title={t('dragToReorder')}
          onKeyDown={event => handleLeafKeyDown(event, leaf, siblings, index, parent)}
        >
          <GripIcon />
        </button>
        <span className="nav-icon" aria-hidden><SidebarIcon name={icon} /></span>
        <span className="sidebar-layout-label" title={leaf.kind === 'link' ? leaf.link.url : label}>{label}</span>
        <span className="sidebar-layout-actions">
          <button
            type="button"
            className="sidebar-layout-icon-btn"
            aria-label={t('hideItem', { label })}
            title={hideDisabled ? t('cannotHideLast') : t('hideItem', { label })}
            disabled={hideDisabled}
            onClick={() => onSetHidden(leaf.id, true)}
          >
            <EyeOffIcon />
          </button>
          {leaf.kind === 'link' ? (
            <button
              type="button"
              className="sidebar-layout-icon-btn"
              aria-label={t('removeLink', { label })}
              title={t('removeLink', { label })}
              onClick={() => onRemoveLink(leaf.id)}
            >
              <CloseIcon />
            </button>
          ) : null}
        </span>
      </li>
    );
  };

  const renderGroupRow = (group: Group, index: number) => {
    const label = groupLabel(group);
    const isRenaming = renamingId === group.id;

    return (
      <li key={group.id} className="sidebar-layout-group">
        <div
          className={rowClass(group, 'group-row')}
          {...dragHandlers(group, dragId && dragId.startsWith('group:') ? { kind: 'before', anchorId: group.id } : { kind: 'into', groupId: group.id })}
        >
          <button
            type="button"
            className="sidebar-layout-handle"
            aria-label={t('reorderGroup', { label })}
            title={t('dragToReorder')}
            onKeyDown={event => handleGroupKeyDown(event, group, index)}
          >
            <GripIcon />
          </button>
          <span className="nav-icon" aria-hidden><SidebarIcon name="folder" /></span>
          {isRenaming ? (
            <input
              ref={renameInputRef}
              className="sidebar-layout-input"
              value={renameValue}
              maxLength={MAX_GROUP_LABEL_LENGTH}
              aria-label={t('groupName')}
              placeholder={t('groupNamePlaceholder')}
              onChange={event => setRenameValue(event.target.value)}
              onBlur={commitRename}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  commitRename();
                } else if (event.key === 'Escape') {
                  event.preventDefault();
                  setRenamingId(null);
                }
              }}
            />
          ) : (
            <span className="sidebar-layout-label" title={label}>{label}</span>
          )}
          <span className="sidebar-layout-actions">
            {!isRenaming ? (
              <button
                type="button"
                className="sidebar-layout-icon-btn"
                aria-label={t('renameGroup', { label })}
                title={t('renameGroup', { label })}
                onClick={() => startRename(group)}
              >
                <PencilIcon />
              </button>
            ) : null}
            <button
              type="button"
              className="sidebar-layout-icon-btn"
              aria-label={t('removeGroup', { label })}
              title={t('removeGroup', { label })}
              onClick={() => onRemoveGroup(group.id)}
            >
              <CloseIcon />
            </button>
          </span>
        </div>
        <ul className="sidebar-layout-list sidebar-layout-group-children">
          {group.children.map((child, childIndex) => renderLeafRow(child, group.children, childIndex, group))}
          {group.children.length === 0 ? (
            <li
              className={`sidebar-layout-dropzone ${overId === `${group.id}:empty` && dragId ? 'drag-over' : ''}`}
              onDragOver={event => {
                event.preventDefault();
                event.stopPropagation();
                setOverId(`${group.id}:empty`);
              }}
              onDrop={event => {
                event.preventDefault();
                event.stopPropagation();
                dropOn({ kind: 'into', groupId: group.id });
              }}
            >
              {t('emptyGroupHint')}
            </li>
          ) : null}
        </ul>
      </li>
    );
  };

  return (
    <div className="sidebar-layout-editor" role="group" aria-label={t('customizeSidebar')}>
      <p className="sidebar-layout-hint">{hint ?? t('customizeHint')}</p>

      <ul className="sidebar-layout-list">
        {visible.map((entry, index) => (
          entry.kind === 'group'
            ? renderGroupRow(entry, index)
            : renderLeafRow(entry, visible, index, null)
        ))}
      </ul>

      {hidden.length > 0 ? (
        <>
          <div className="sidebar-layout-section">{t('hiddenItems')}</div>
          <ul className="sidebar-layout-list">
            {hidden.map(leaf => {
              const label = leafLabel(leaf);
              return (
                <li key={leaf.id} className="sidebar-layout-row hidden-row">
                  <span className="sidebar-layout-handle sidebar-layout-handle-placeholder" aria-hidden />
                  <span className="nav-icon" aria-hidden><SidebarIcon name={leaf.kind === 'item' ? leaf.item.icon : 'external'} /></span>
                  <span className="sidebar-layout-label" title={label}>{label}</span>
                  <span className="sidebar-layout-actions">
                    <button
                      type="button"
                      className="sidebar-layout-icon-btn"
                      aria-label={t('showItem', { label })}
                      title={t('showItem', { label })}
                      onClick={() => onSetHidden(leaf.id, false)}
                    >
                      <EyeIcon />
                    </button>
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      ) : null}

      {addingLink ? (
        <form
          className="sidebar-layout-form"
          onSubmit={event => {
            event.preventDefault();
            submitLink();
          }}
        >
          <input
            className="sidebar-layout-input"
            value={linkLabel}
            maxLength={MAX_LINK_LABEL_LENGTH}
            aria-label={t('linkLabel')}
            placeholder={t('linkLabelPlaceholder')}
            onChange={event => setLinkLabel(event.target.value)}
          />
          <input
            className="sidebar-layout-input"
            value={linkUrl}
            maxLength={MAX_LINK_URL_LENGTH}
            aria-label={t('linkUrl')}
            placeholder={t('linkUrlPlaceholder')}
            inputMode="url"
            autoFocus
            onChange={event => {
              setLinkUrl(event.target.value);
              setLinkError(null);
            }}
          />
          {linkError ? <p className="sidebar-layout-error" role="alert">{linkError}</p> : null}
          <div className="sidebar-layout-footer">
            <button
              type="button"
              className="sidebar-layout-btn"
              onClick={() => {
                setAddingLink(false);
                setLinkError(null);
              }}
            >
              {t('cancel')}
            </button>
            <button type="submit" className="sidebar-layout-btn sidebar-layout-btn-primary">{t('addLinkSubmit')}</button>
          </div>
        </form>
      ) : (
        <div className="sidebar-layout-toolbar">
          <button
            type="button"
            className="sidebar-layout-btn sidebar-layout-btn-ghost"
            onClick={() => {
              const id = createSidebarGroupId();
              onAddGroup(id, '');
              setRenamingId(id);
              setRenameValue('');
            }}
          >
            <PlusIcon />
            {t('newGroup')}
          </button>
          <button type="button" className="sidebar-layout-btn sidebar-layout-btn-ghost" onClick={() => setAddingLink(true)}>
            <PlusIcon />
            {t('addLink')}
          </button>
        </div>
      )}

      <div className="sidebar-layout-footer">
        <button
          type="button"
          className="sidebar-layout-btn"
          onClick={onReset}
          disabled={!canReset}
        >
          {resetLabel ?? t('customizeReset')}
        </button>
        {onDone ? (
          <button
            type="button"
            className="sidebar-layout-btn sidebar-layout-btn-primary"
            onClick={onDone}
          >
            {t('customizeDone')}
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default SidebarLayoutEditor;
